import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, 
  Trash2, 
  MapPin,
  Loader2,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default hotspot icons from user's images
const DEFAULT_HOTSPOTS = [
  {
    id: 'equipment',
    name: 'Equipment',
    icon_url: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/2mp799up_hs_gray_2.png',
    description: 'Equipment or asset location',
    color: '#4CAF50'
  },
  {
    id: 'navigation',
    name: 'Navigation',
    icon_url: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/cvver25m_hs_gray_3.png',
    description: 'Navigation point',
    color: '#2196F3'
  },
  {
    id: 'info',
    name: 'Info/Quiz',
    icon_url: 'https://customer-assets.emergentagent.com/job_817bdca5-cb3c-4cda-bf89-74ce1f443d99/artifacts/cyu7xupi_hs_gray_1.png',
    description: 'Information or quiz point',
    color: '#FF9800'
  }
];

export function HotspotTypeSelector({ value, onChange, tourId }) {
  const { token } = useAuth();
  const [types, setTypes] = useState(DEFAULT_HOTSPOTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTypes = async () => {
      if (!token) {
        setTypes(DEFAULT_HOTSPOTS);
        setLoading(false);
        return;
      }
      
      try {
        const url = tourId 
          ? `${API_URL}/api/elearning/hotspot-types?tour_id=${tourId}`
          : `${API_URL}/api/elearning/hotspot-types`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTypes(data.length > 0 ? data : DEFAULT_HOTSPOTS);
        } else {
          setTypes(DEFAULT_HOTSPOTS);
        }
      } catch (err) {
        console.error('Failed to fetch hotspot types:', err);
        setTypes(DEFAULT_HOTSPOTS);
      } finally {
        setLoading(false);
      }
    };

    fetchTypes();
  }, [token, tourId]);

  if (loading) {
    return <div className="h-10 bg-muted animate-pulse rounded" />;
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder="Select hotspot type" />
      </SelectTrigger>
      <SelectContent>
        {types.map((type) => (
          <SelectItem key={type.id} value={type.id}>
            <div className="flex items-center gap-2">
              <img 
                src={type.icon_url} 
                alt={type.name}
                className="w-5 h-5 object-contain"
              />
              <span>{type.name}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function HotspotIcon({ type, size = 32, className = '' }) {
  const hotspot = DEFAULT_HOTSPOTS.find(h => h.id === type) || DEFAULT_HOTSPOTS[0];
  
  return (
    <img 
      src={hotspot.icon_url}
      alt={hotspot.name}
      width={size}
      height={size}
      className={`object-contain ${className}`}
      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
    />
  );
}

export function HotspotTypeManager({ tourId, isOpen, onClose }) {
  const { token, user } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newType, setNewType] = useState({
    name: '',
    icon_url: '',
    description: '',
    color: '#ffffff'
  });

  const isAdmin = user?.role === 'admin' || user?.role === 'editor';

  const fetchTypes = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const url = tourId 
        ? `${API_URL}/api/elearning/hotspot-types?tour_id=${tourId}`
        : `${API_URL}/api/elearning/hotspot-types`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTypes(data.length > 0 ? data : DEFAULT_HOTSPOTS);
      }
    } catch (err) {
      console.error('Failed to fetch hotspot types:', err);
    } finally {
      setLoading(false);
    }
  }, [token, tourId]);

  useEffect(() => {
    if (isOpen) {
      fetchTypes();
    }
  }, [isOpen, fetchTypes]);

  const handleCreate = async () => {
    if (!newType.name || !newType.icon_url) return;
    
    try {
      const res = await fetch(`${API_URL}/api/elearning/hotspot-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newType,
          tour_id: tourId
        })
      });
      
      if (res.ok) {
        await fetchTypes();
        setIsCreating(false);
        setNewType({ name: '', icon_url: '', description: '', color: '#ffffff' });
      }
    } catch (err) {
      console.error('Failed to create hotspot type:', err);
    }
  };

  const handleDelete = async (typeId) => {
    if (!confirm('Delete this hotspot type?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/elearning/hotspot-types/${typeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        await fetchTypes();
      }
    } catch (err) {
      console.error('Failed to delete hotspot type:', err);
    }
  };

  const initDefaults = async () => {
    try {
      const res = await fetch(`${API_URL}/api/elearning/hotspot-types/init-defaults`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        await fetchTypes();
      }
    } catch (err) {
      console.error('Failed to initialize defaults:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Hotspot Types
          </DialogTitle>
          <DialogDescription>
            Customize hotspot icons for the 3D viewer
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px]">
          <div className="space-y-4 p-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : isCreating ? (
              <Card className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={newType.name}
                    onChange={(e) => setNewType(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Safety Point"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Icon URL</Label>
                  <Input
                    value={newType.icon_url}
                    onChange={(e) => setNewType(prev => ({ ...prev, icon_url: e.target.value }))}
                    placeholder="https://..."
                  />
                  {newType.icon_url && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <img 
                        src={newType.icon_url} 
                        alt="Preview"
                        className="w-8 h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <span className="text-sm text-muted-foreground">Preview</span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={newType.description}
                    onChange={(e) => setNewType(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What this hotspot represents"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newType.color}
                      onChange={(e) => setNewType(prev => ({ ...prev, color: e.target.value }))}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={newType.color}
                      onChange={(e) => setNewType(prev => ({ ...prev, color: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreate} disabled={!newType.name || !newType.icon_url}>
                    Create
                  </Button>
                  <Button variant="outline" onClick={() => setIsCreating(false)}>
                    Cancel
                  </Button>
                </div>
              </Card>
            ) : (
              <>
                {types.length === 0 ? (
                  <div className="text-center py-8">
                    <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium mb-2">No Custom Hotspots</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create custom hotspot icons for your tour
                    </p>
                    <Button onClick={initDefaults} variant="outline" className="mr-2">
                      Load Defaults
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {types.map((type) => (
                      <div 
                        key={type.id}
                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <img 
                          src={type.icon_url}
                          alt={type.name}
                          className="w-10 h-10 object-contain"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{type.name}</div>
                          {type.description && (
                            <div className="text-sm text-muted-foreground">{type.description}</div>
                          )}
                        </div>
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: type.color }}
                        />
                        {isAdmin && !type.is_default && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(type.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                        {type.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          {isAdmin && !isCreating && types.length > 0 && (
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Custom
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default HotspotTypeSelector;
